import { createDraft, type Draft, finishDraft } from 'immer';
import type { StoreApi } from 'zustand';

/**
 * A utility that creates performant CRUD-style object setters for Zustand stores.
 * Designed for maps or collections like { [id]: Entity } slices.
 *
 * @template T - The root store state type
 * @template K - The key in the store state that holds the object map
 * @template V - The value type of the object map entries
 *
 * @param key - The key in the store state that holds the object map
 * @param set - The Zustand setState function
 *
 * @example
 *   const { setSingle, remove } = createObjectSetters('orders', set);
 *   setSingle('id123', { price: 100 });
 *
 * @returns An object containing CRUD operations for the store slice
 */
function createObjectSetters<
  T extends object,
  K extends keyof T,
  V extends T[K] extends Record<string, infer U> ? U : never,
>(key: K, set: StoreApi<T>['setState']) {
  type MapType = Record<string, V>;

  const getExistingMap = (state: T): MapType => (state[key] ?? {}) as MapType;

  /**
   * Creates a partial state update for the store
   */
  const createUpdate = (value: MapType): Partial<T> => {
    const update = {} as Partial<T>;
    update[key] = value as T[K];
    return update;
  };

  /**
   * Sets or replaces a single entry by ID.
   * @param id - The unique identifier for the entry
   * @param value - The value to set
   */
  const setSingle = (id: string, value: V) =>
    set((state) => {
      const currentMap = getExistingMap(state);
      const nextMap: MapType = {
        ...currentMap,
        [id]: value,
      };
      return createUpdate(nextMap);
    });

  /**
   * Merges multiple entries into the map.
   * @param newValues - Object containing entries to merge
   */
  const setMultiple = (newValues: Partial<T[K]>) =>
    set((state) => {
      const currentMap = getExistingMap(state);
      const nextMap: MapType = {
        ...currentMap,
        ...(newValues as Record<string, V>),
      };
      return createUpdate(nextMap);
    });

  /**
   * Removes a single entry by ID.
   * @param id - The unique identifier of the entry to remove
   */
  const remove = (id: string) =>
    set((state) => {
      const currentMap = getExistingMap(state);
      const { [id]: _, ...rest } = currentMap;
      return createUpdate(rest);
    });

  /**
   * Removes multiple entries by their IDs.
   * @param ids - Array of unique identifiers to remove
   */
  const removeMultiple = (ids: string[]) =>
    set((state) => {
      const currentMap = getExistingMap(state);
      const idSet = new Set(ids);

      // Use Object.entries to avoid dynamic property deletion
      const filtered = Object.fromEntries(
        Object.entries(currentMap).filter(([id]) => !idSet.has(id)),
      ) as MapType;

      return createUpdate(filtered);
    });

  /**
   * Updates a single entry in-place using Immer (for nested structures).
   * @template PayloadType - The type of the value being updated
   * @param id - The unique identifier of the entry to update
   * @param mutator - A function that receives a draft of the current value and can mutate it
   */
  const updateSingle = <PayloadType extends Record<string, unknown>>(
    id: string,
    mutator: (draft: Draft<PayloadType>) => void,
  ) =>
    set((state) => {
      const currentMap = getExistingMap(state);
      const existingValue = currentMap[id] ?? ({} as V);

      // Use Immer for safe nested mutation
      const draft = createDraft(existingValue as unknown as PayloadType);
      mutator(draft);
      const updated = finishDraft(draft) as V;

      const nextMap: MapType = {
        ...currentMap,
        [id]: updated,
      };
      return createUpdate(nextMap);
    });

  return { setSingle, setMultiple, remove, removeMultiple, updateSingle };
}

export default createObjectSetters;
